import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Invitation, RoleType } from '@promo/shared';
import * as api from '@/lib/api';

// Query key factory
const invitationsKeys = {
  all: ['invitations'] as const,
  lists: () => [...invitationsKeys.all, 'list'] as const,
  list: (packetId: string, role?: RoleType) =>
    [...invitationsKeys.lists(), packetId, role] as const,
};

// Hook to fetch invitations for a packet
export function useInvitations(
  packetId: string,
  role?: RoleType,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: invitationsKeys.list(packetId, role),
    queryFn: async () => {
      const params: Record<string, string> = { packetId };
      if (role) {
        params.role = role;
      }
      
      return api.get<Invitation[]>('/invitations', params);
    },
    enabled: options?.enabled ?? !!packetId,
  });
}

// Hook to send invitations
export function useSendInvitations() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      packetId,
      inviteeEmails,
      roleToInvite,
    }: {
      packetId: string;
      inviteeEmails: string[];
      roleToInvite: RoleType;
    }) => {
      return api.post('/invitations', {
        packetId,
        inviteeEmails,
        roleToInvite,
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: invitationsKeys.list(variables.packetId, variables.roleToInvite),
      });
    },
  });
} 